import {
  Link as TanStackLink,
  Navigate as TanStackNavigate,
  Outlet,
  RouterProvider,
  createBrowserHistory,
  createBrowserHistory as createTanStackBrowserHistory,
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  useLocation as useTanStackLocation,
  useNavigate as useTanStackNavigate,
  useParams as useTanStackParams,
  useRouterState,
  type AnyRoute,
  type AnyRouter,
  type RouterHistory,
} from '@tanstack/react-router';
import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type ReactNode,
} from 'react';

export type TovRouteLoaderInput = {
  location: {
    searchStr?: string;
  };
  params: Record<string, string | undefined>;
};

export interface TovRouteObject {
  children?: TovRouteObject[];
  element?: ReactNode;
  errorElement?: ReactNode;
  index?: boolean;
  loader?: (input: TovRouteLoaderInput) => unknown | Promise<unknown>;
  path?: string;
}

export type RouteObject = TovRouteObject;

export interface TovRouterOptions {
  basename?: string;
  history?: RouterHistory;
}

export type TovRouter = AnyRouter;

function normalizeTovRoutePath(path: string | undefined, index?: boolean) {
  if (index) {
    return '/';
  }

  const normalizedPath = path?.replace(/^\/+/, '') ?? '/';

  if (normalizedPath === '') {
    return '';
  }

  if (normalizedPath === '*') {
    return '$';
  }

  return normalizedPath
    .split('/')
    .map((segment) => {
      if (segment === '*') {
        return '$';
      }
      if (segment.startsWith(':')) {
        return `$${segment.slice(1)}`;
      }
      return segment;
    })
    .join('/');
}

function TovRouteComponent({ element }: { element: ReactNode }) {
  return <>{element}</>;
}

function TovRouteErrorComponent({ errorElement }: { errorElement: ReactNode }) {
  return <>{errorElement}</>;
}

function createTovRoute(
  routeObject: TovRouteObject,
  parentRoute: AnyRoute,
  routeId: string,
): AnyRoute {
  const routePath = normalizeTovRoutePath(routeObject.path, routeObject.index);
  const route = createRoute({
    getParentRoute: () => parentRoute,
    ...(routePath === ''
      ? {
          id: routeId,
        }
      : {
          path: routePath,
        }),
    component: () =>
      routeObject.element ? (
        <TovRouteComponent element={routeObject.element} />
      ) : (
        <Outlet />
      ),
    ...(routeObject.errorElement
      ? {
          errorComponent: () => (
            <TovRouteErrorComponent errorElement={routeObject.errorElement} />
          ),
        }
      : {}),
    ...(routeObject.loader
      ? {
          loader: routeObject.loader,
        }
      : {}),
  }) as AnyRoute;

  if (routeObject.children && routeObject.children.length > 0) {
    return route.addChildren(
      routeObject.children.map((child, index) =>
        createTovRoute(child, route, `${routeId}-${index}`),
      ),
    ) as AnyRoute;
  }

  return route;
}

export function createTovRouter(
  routeObjects: TovRouteObject[],
  options: TovRouterOptions = {},
) {
  const rootRoute = createRootRoute({
    component: Outlet,
  });
  const routeTree = rootRoute.addChildren(
    routeObjects.map((routeObject, index) =>
      createTovRoute(routeObject, rootRoute, `route-${index}`),
    ),
  );

  return createRouter({
    history:
      options.history ??
      (options.basename ? createTanStackBrowserHistory() : undefined),
    routeTree,
    ...(options.basename
      ? {
          basepath: options.basename,
        }
      : {}),
  });
}

export { Outlet, RouterProvider, createBrowserHistory, createMemoryHistory };

const TanStackLinkCompat = TanStackLink as ComponentType<
  TovLinkProps & Record<string, unknown>
>;

interface NavLinkState {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
}

type TovLinkProps = Omit<
  ComponentPropsWithoutRef<'a'>,
  'className' | 'href'
> & {
  activeProps?: unknown;
  className?: string;
  end?: boolean;
  inactiveProps?: unknown;
  replace?: boolean;
  state?: unknown;
  to: string;
};

export const Link = forwardRef<HTMLAnchorElement, TovLinkProps>(
  function Link(props, ref) {
    const { end, ...linkProps } = props;
    void end;

    return <TanStackLinkCompat {...linkProps} ref={ref} />;
  },
);

type TovNavLinkProps = Omit<
  TovLinkProps,
  'activeProps' | 'className' | 'inactiveProps'
> & {
  className?: string | ((state: NavLinkState) => string | undefined);
};

export const NavLink = forwardRef<HTMLAnchorElement, TovNavLinkProps>(
  function NavLink({ className, ...props }, ref) {
    const { end, ...linkProps } = props;
    void end;

    if (typeof className !== 'function') {
      return (
        <TanStackLinkCompat {...linkProps} className={className} ref={ref} />
      );
    }

    return (
      <TanStackLinkCompat
        {...linkProps}
        activeProps={() => ({
          className: className({
            isActive: true,
            isPending: false,
            isTransitioning: false,
          }),
        })}
        inactiveProps={() => ({
          className: className({
            isActive: false,
            isPending: false,
            isTransitioning: false,
          }),
        })}
        ref={ref}
      />
    );
  },
);

type NavigateProps = {
  replace?: boolean;
  to: string;
};

export function Navigate({ replace, to }: NavigateProps) {
  // TanStack Navigate starts navigation when its props object changes. Keep the
  // element stable when an occasion or location subscription rerenders a parent.
  return useMemo(
    () => <TanStackNavigate replace={replace} to={to} />,
    [replace, to],
  );
}

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
};

export function useNavigate() {
  const navigate = useTanStackNavigate();

  return (
    to: string | { replace?: boolean; to: string },
    options: NavigateOptions = {},
  ) => {
    if (typeof to === 'string') {
      return navigate({
        replace: options.replace,
        state: options.state as never,
        to,
      });
    }

    return navigate({
      replace: to.replace,
      to: to.to,
    });
  };
}

export function useLocation() {
  const location = useTanStackLocation();
  return useMemo(
    () => ({ ...location, search: location.searchStr }),
    [location],
  );
}

export function useParams<
  TParams extends Record<string, string | undefined>,
>() {
  return useTanStackParams({
    strict: false,
  }) as TParams;
}

type SearchParamInit =
  | string
  | URLSearchParams
  | Record<string, string | number | boolean | null | undefined>
  | ((current: URLSearchParams) => URLSearchParams | string);

function createUrlSearchParams(
  init: SearchParamInit,
  current: URLSearchParams,
) {
  const nextInit = typeof init === 'function' ? init(current) : init;

  if (nextInit instanceof URLSearchParams) {
    return new URLSearchParams(nextInit);
  }

  if (typeof nextInit === 'string') {
    return new URLSearchParams(nextInit);
  }

  const next = new URLSearchParams();
  Object.entries(nextInit).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      next.set(key, String(value));
    }
  });
  return next;
}

export function useSearchParams(): [
  URLSearchParams,
  (init: SearchParamInit, options?: NavigateOptions) => void,
] {
  const location = useRouterState({
    select: (state) => state.location,
  });
  const navigate = useTanStackNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.searchStr),
    [location.searchStr],
  );

  return [
    searchParams,
    (init, options = {}) => {
      const next = createUrlSearchParams(init, searchParams);
      const searchString = next.toString();
      const hash = location.hash ? `#${location.hash}` : '';

      void navigate({
        replace: options.replace,
        state: options.state as never,
        to: `${location.pathname}${searchString ? `?${searchString}` : ''}${hash}`,
      });
    },
  ];
}

export function useRouteError() {
  return useRouterState({
    select: (state) => state.matches.at(-1)?.error,
  });
}

function matchRoutePattern(pattern: string, pathname: string) {
  const patternSegments = pattern.replace(/^\/+|\/+$/g, '').split('/');
  const pathSegments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const params: Record<string, string> = {};

  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index];
    const pathSegment = pathSegments[index];

    if (patternSegment === '*') {
      return { params };
    }

    if (!pathSegment) {
      return null;
    }

    if (patternSegment.startsWith(':')) {
      params[patternSegment.slice(1)] = decodeURIComponent(pathSegment);
      continue;
    }

    if (patternSegment !== pathSegment) {
      return null;
    }
  }

  return patternSegments.length === pathSegments.length ? { params } : null;
}

export function useMatch(pattern: string) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  return useMemo(
    () => matchRoutePattern(pattern, pathname),
    [pattern, pathname],
  );
}

export function TovRouterDevtools({ router }: { router: TovRouter }) {
  const [Devtools, setDevtools] = useState<ComponentType<{
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    router: TovRouter;
  }> | null>(null);
  const isProduction = Boolean(
    (import.meta as unknown as { env?: { PROD?: boolean } }).env?.PROD,
  );

  useEffect(() => {
    if (isProduction) {
      return;
    }

    let isMounted = true;
    void import('@tanstack/router-devtools').then((module) => {
      if (isMounted) {
        setDevtools(() => module.TanStackRouterDevtools);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isProduction]);

  if (isProduction || !Devtools) {
    return null;
  }

  return <Devtools position="bottom-right" router={router} />;
}

export type { TovLinkProps };
